import {IOnlineCondolenceDetails} from '../../types/resources/OnlineCondolenceDetails';
import styled from 'styled-components';
import OnlineCondolenceUtils from '../../utils/OnlineCondolenceUtils';
import ImageBlock from '../ImageBlock';
import {ImageSize} from '../../enums/ImageSize';
import Title from './Title';
import Stats from './Stats';
import LifeDates from './LifeDates';
import {ReactComponent as FacebookIcon} from "../../icons/facebook.svg";
import {ReactComponent as XTwitterIcon} from "../../icons/x-twitter.svg";
import {ReactComponent as WhatsAppIcon} from "../../icons/whatsapp.svg";
import {ReactComponent as MailIcon} from "../../icons/mail.svg";
import { useCandleContext } from "../../context/candle/useCandleContext";
import {useSettingsContext} from '../../context/settings/useSettingsContext';
import {usePostsContext} from '../../context/posts/usePostsContext';
import {EmailShareButton, FacebookShareButton, TwitterShareButton, WhatsappShareButton} from 'react-share';
import ActionLinks from './ActionLinks';

const Banner = styled.div<{ $imageUrl: string }>`
    background-color: var(--cr-primary-color);
    background-image: url(${props => props.$imageUrl});
    background-position: center;
    background-size: cover;
    height: 350px;
`

const PersonalInfo = styled.div`
    min-height: calc(var(--cr-image-size-large)/2);
    position: relative;

    > * {
      flex: 1;
    }

    @media (max-width: 768px) {
        &&& {
            flex-direction: column;
            gap: var(--cr-spacing-lg);
            padding-top: calc(var(--cr-image-size-large) / 2);
        }
    }
`

const ShiftedImageBlock = styled.div`
    left: var(--cr-spacing-lg);
    outline: 8px solid var(--cr-grey-light-color);
    outline-offset: -1px;
    position: absolute;
    top: 0;
    transform: translateY(-50%);

    @media (max-width: 768px) {
        left: 50%;
        transform: translateX(-50%) translateY(-50%);
    }
`

const StatsWrapper = styled.div`
    @media (max-width: 768px) {
        && {
            justify-content: center;
        }
    }
`

const Quote = styled.div`
`

const Socials = styled.div`
    > * {
      transform: translateY(-50%);
    }
`

type TProps = {
    details: IOnlineCondolenceDetails;
}

const Header = ({details}: TProps) => {
    const { publicUrl, headerFallbackImage, condoFallbackImage } = useSettingsContext();
    const { candleCount } = useCandleContext();
    const { postsCount } = usePostsContext();

    const fullName = OnlineCondolenceUtils.getFullName(details);

    return (
        <>
            <Banner className={'cr-banner'} $imageUrl={
                details.backgroundFileUrl
                || headerFallbackImage
                || ''
            }/>
            <div className="cr-condolence-header cr-condolence-header-bg cr-bg-grey-light">
                <PersonalInfo className="cr-flex cr-justify-between cr-container">
                    <ShiftedImageBlock className="cr-rounded-block cr-bg-grey-light">
                        <ImageBlock
                            src={
                                details.imageFileUrl
                                || condoFallbackImage
                                || `${publicUrl}/placeholder-condolence.jpg`
                            }
                            placeholderSrc={
                                condoFallbackImage
                                || `${publicUrl}/placeholder-condolence.jpg`
                            }
                            alt={fullName}
                            size={ImageSize.Large}
                        />
                    </ShiftedImageBlock>
                    <div>{/* Dummy. */}</div>
                    <div className="cr-flex cr-flex-col cr-center cr-gap">
                        <Title fullName={fullName}/>
                        <LifeDates
                            dateOfBirth={details.dateOfBirth}
                            dateOfDeath={details.dateOfDeath}
                        />
                    </div>
                    <StatsWrapper className="cr-flex cr-align-center cr-justify-end">
                        <Stats
                            numberOfCandles={candleCount}
                            numberOfPosts={postsCount}
                            numberOfLikes={details.numberOfLikes}
                        />
                    </StatsWrapper>
                </PersonalInfo>
                {details.quote &&
                    <Quote className="cr-flex cr-center cr-text-lg cr-text-primary cr-p-xl">
                        {details.quote}
                    </Quote>
                }
                <ActionLinks details={details} />
            </div>
            <div className={'cr-condolence-header-bottom'}>
                <div className="cr-condolence-header-bottom-inner cr-condolence-header-bg cr-bg-grey-light cr-masked-full cr-p-lg"></div>
            </div>
            <Socials className="cr-socials cr-flex cr-center cr-gap-sm cr-text-white">
                <FacebookShareButton url={window.location.href}>
                    <FacebookIcon className="cr-bg-primary cr-rounded-full"/>
                </FacebookShareButton>
                <TwitterShareButton url={window.location.href}>
                    <XTwitterIcon className="cr-bg-primary cr-rounded-full"/>
                </TwitterShareButton>
                <WhatsappShareButton url={window.location.href}>
                    <WhatsAppIcon className="cr-bg-primary cr-rounded-full"/>
                </WhatsappShareButton>
                <EmailShareButton url={window.location.href}>
                    <MailIcon className="cr-bg-primary cr-rounded-full"/>
                </EmailShareButton>
            </Socials>
        </>
    );
}

export default Header;
